// BJJ-specific taxonomy + constants for the Combatives Detailed Journal.
// Locked taxonomy per user spec. See MASTER_BACKLOG.md.
//
// Position is a 3-level cascading structure:
//   { primary: 'Guard', secondary: 'Open', tertiary: 'Butterfly' }
// Secondary is required when the primary has sub-positions; tertiary is
// optional, only present when the secondary defines specifics.
//
// Submissions live as a free-entry field on each technique row (with
// autocomplete from BJJ_COMMON_SUBMISSIONS). Position→submission
// correlations become queryable from this shape.

export const BJJ_CLASS_FORMATS = ['Gi', 'No-Gi'];

export const BJJ_FOCUS_MODES = [
  'Drilling',
  'Live Rolling',
  'Comp Training',
  'Open Mat',
  'Conditioning',
];

// Focus modes that should reveal the Sparring section.
export const BJJ_SPARRING_TRIGGERS = ['Live Rolling', 'Comp Training'];

// Per-technique self-evaluation. Five-step learning progression — BJJ-native
// language, action-oriented, anti-shame-aware ("Studying" is honest, not
// "Beginner" which feels like a permanent label).
export const BJJ_MASTERY_LEVELS = [
  { value: 'Studying', label: 'Studying', help: "Heard of it, watching video, not yet drilled" },
  { value: 'Drilling', label: 'Drilling', help: "Can do it slowly with a cooperative partner" },
  { value: 'Working', label: 'Working', help: "Landing it sometimes in flow rolling" },
  { value: 'Hitting Live', label: 'Hitting Live', help: "Landing it in free rolling" },
  { value: 'Default Move', label: 'Default Move', help: "Reflexive, go-to weapon in this position" },
];

// Felt scale — per-sparring self-rating. Anti-shame: "Got smashed" is
// honest but neutral, not "I failed."
export const BJJ_FELT_SCALE = [
  { value: 1, label: 'Got smashed' },
  { value: 2, label: 'Held my own' },
  { value: 3, label: 'Even' },
  { value: 4, label: 'Pushed the pace' },
  { value: 5, label: 'Dominated' },
];

// 3-level position taxonomy. Confirmed by user.
// Each primary has `secondaries[]`; each secondary is either a bare string
// (no tertiaries) or an object `{ name, tertiaries: [...] }`.
export const BJJ_POSITIONS = [
  {
    primary: 'Standing / Clinch',
    secondaries: ['Takedowns', 'Throws', 'Trips', 'Guard Pulls'],
  },
  {
    primary: 'Guard',
    label: 'Guard (Bottom)',
    secondaries: [
      'Closed',
      { name: 'Half', tertiaries: ['Knee Shield', 'Deep Half', 'Z-Guard'] },
      {
        name: 'Open',
        tertiaries: [
          'Butterfly',
          'De La Riva',
          'Reverse De La Riva',
          'Spider',
          'Lasso',
          'X-Guard',
          'Single Leg X',
          'Worm Guard',
          'Lapel Guard',
          'Sit-up Guard',
        ],
      },
    ],
  },
  {
    primary: 'Passing',
    label: 'Passing (Top)',
    secondaries: [
      'Torreando (Bullfighter)',
      'Knee Cut / Knee Slice',
      'Leg Weave',
      'Pressure / Smash Pass',
      'Stack Pass',
      'Long Step',
      'Over-Under',
      'Body Lock',
      'Headquarters',
    ],
  },
  {
    primary: 'Side Control',
    secondaries: [
      'Top',
      {
        name: 'Bottom (Escapes)',
        tertiaries: [
          'Frame & Shrimp to Guard',
          'Underhook & Run',
          'Roll to Knees',
          'Hip Heist',
        ],
      },
      'Transitions',
    ],
  },
  {
    primary: 'Mount',
    secondaries: [
      'Top — Standard',
      'Top — High Mount',
      'Top — Low Mount',
      'Top — S-Mount',
      'Top — Technical Mount',
      {
        name: 'Bottom (Escapes)',
        tertiaries: [
          'Elbow Escape (Shrimp)',
          'Bridge & Roll (Trap and Roll / Upa)',
          'Knee Lever',
          'Heel Drag',
          'Bench Press Escape',
        ],
      },
    ],
  },
  {
    // Back primary — restructured. Previously mixed "Offense — RNC"
    // alongside "Seatbelt Control" / "Body Triangle" which is comparing
    // apples (attack flows) to oranges (control mechanics). Now: Offense
    // is one secondary with attack-chain tertiaries; control mechanics
    // are their own secondaries; Defense / Escapes gets explicit
    // tertiaries so users can record specific escapes.
    primary: 'Back',
    secondaries: [
      {
        name: 'Offense',
        tertiaries: [
          'RNC / Choke Chains',
          'Bow & Arrow',
          'Crucifix',
          'Rolling Back Take Chains',
        ],
      },
      'Seatbelt Control',
      'Body Triangle',
      {
        name: 'Defense / Escapes',
        tertiaries: [
          'Scrape the Hook',
          'Hand-Fight',
          'Bridge to Shoulder',
          'Recompose Guard',
        ],
      },
    ],
  },
  {
    primary: 'Knee on Belly',
    secondaries: [
      'Top',
      {
        name: 'Bottom (Escapes)',
        tertiaries: [
          'Hand-Fight & Hip Shift',
          'Trap the Knee',
          'Shrimp to Guard',
        ],
      },
    ],
  },
  {
    primary: 'Turtle',
    secondaries: [
      'Top — Breakdown Attacks',
      'Top — Back Take',
      {
        name: 'Bottom (Escapes)',
        tertiaries: [
          'Sit-out',
          'Switch Base',
          'Recover Guard',
          'Roll Forward',
        ],
      },
    ],
  },
  {
    // Leg Entanglements — both players are entangled simultaneously, so
    // Top/Bottom doesn't apply (founder correction). Instead,
    // each entanglement secondary gets Attacking / Defending / Escaping
    // tertiaries so the offense/defense/escape dynamic is captured at the
    // right layer without misusing the Top label.
    primary: 'Leg Entanglements',
    secondaries: [
      { name: 'Ashi Garami (Single Leg X)', tertiaries: ['Attacking', 'Defending', 'Escaping'] },
      { name: 'Outside Ashi', tertiaries: ['Attacking', 'Defending', 'Escaping'] },
      { name: 'Inside Sankaku', tertiaries: ['Attacking', 'Defending', 'Escaping'] },
      { name: '50/50', tertiaries: ['Attacking', 'Defending', 'Escaping'] },
      { name: 'Honey Hole / Saddle', tertiaries: ['Attacking', 'Defending', 'Escaping'] },
      { name: 'Reaping Position', tertiaries: ['Attacking', 'Defending', 'Escaping'] },
      { name: 'Cross Ashi', tertiaries: ['Attacking', 'Defending', 'Escaping'] },
    ],
  },
];

// Starter list for the per-technique `submission` autocomplete. Users can
// type any custom submission — this list is suggestions only, not a hard
// taxonomy.
export const BJJ_COMMON_SUBMISSIONS = [
  // Chokes
  'Rear Naked Choke',
  'Bow & Arrow',
  'Triangle',
  'Anaconda',
  "D'arce",
  'Guillotine',
  'Ezekiel',
  'Lapel Choke',
  'Loop Choke',
  'Cross Collar Choke',
  'Brabo',
  'Arm Triangle',
  'Paper Cutter',
  'North-South Choke',
  // Joint locks — upper body
  'Armbar',
  'Kimura',
  'Americana',
  'Omoplata',
  'Wrist Lock',
  // Joint locks — lower body
  'Straight Footlock',
  'Inside Heel Hook',
  'Outside Heel Hook',
  'Kneebar',
  'Toe Hold',
  // Compressions / less common
  'Calf Slicer',
  'Bicep Slicer',
];

// ---------- Helpers ----------

// Get the secondaries array for a primary position name.
// Returns an array of either strings or { name, tertiaries } objects.
export const getSecondariesFor = (primary) => {
  const p = BJJ_POSITIONS.find((x) => x.primary === primary);
  return p ? p.secondaries : [];
};

// Get the secondary name strings only (for dropdowns).
export const getSecondaryNames = (primary) => {
  return getSecondariesFor(primary).map((s) => (typeof s === 'string' ? s : s.name));
};

// Get the tertiary names for a given primary+secondary, or [] if none.
export const getTertiariesFor = (primary, secondary) => {
  const secs = getSecondariesFor(primary);
  const match = secs.find((s) => (typeof s === 'string' ? s === secondary : s.name === secondary));
  if (!match || typeof match === 'string') return [];
  return match.tertiaries || [];
};

// Format a position object as a slash-delimited string for display.
// { primary: 'Guard', secondary: 'Open', tertiary: 'Butterfly' } → "Guard / Open / Butterfly"
export const formatPosition = (pos) => {
  if (!pos) return '';
  if (typeof pos === 'string') return pos; // legacy string
  const parts = [pos.primary, pos.secondary, pos.tertiary].filter(Boolean);
  return parts.join(' / ');
};

// A blank technique row for "+ Add technique" — keeps shape stable.
// `weapon` and `weaponMode` are null when this isn't a weapons technique.
// Per-technique flag (not session-level) so a user can record one weapons
// drill alongside several non-weapons techniques in the same session.
//
// `playerSide` + `nextPosition` (refined) — Game-Maps-forward
// data capture.
//   - `playerSide` is a DERIVED field, never user-input. It is auto-set
//     on save by calling `inferPlayerSide(position)` inside BJJLogDialog's
//     handleSave. Position picker is the single source of truth; the user
//     never has to restate orientation. See inferPlayerSide() above for
//     the derivation rules.
//   - `nextPosition` is the optional "where did this transition take you"
//     capture — feeds Game Map edges. Same { primary, secondary, tertiary,
//     playerSide } shape, with playerSide also derived on save.
// Both nullable so older sessions stay valid and the schema delta is
// non-breaking.
export const newTechniqueRow = () => ({
  position: { primary: '', secondary: '', tertiary: '' },
  playerSide: null,              // derived from position on save
  nextPosition: null,            // { primary, secondary, tertiary, playerSide } | null
  name: '',
  weapon: null,
  weaponMode: null,        // 'Offensive' | 'Defensive' | null
  submission: '',
  steps: [],
  detail: '',
  reps: '',
  mastery: 'Drilling',
});

// Derive the user's orientation (Top / Bottom / Neutral / null) for a
// given position. Reads:
//   1. The primary's `label` if present (Guard → Bottom, Passing → Top).
//   2. The secondary string for explicit prefixes ("Top — …",
//      "Bottom (Escapes)", "Offense", "Defense / Escapes").
//   3. Tertiary roles ("Attacking" → Top, "Defending"/"Escaping" → Bottom)
//      for Leg Entanglements where the secondary is neutral.
//   4. Standing/Clinch + Leg Entanglements default to Neutral (both
//      players standing or both entangled).
// Returns null only when called with an incomplete position.
//
// playerSide is treated as a DERIVED field — saved alongside techniques
// but auto-computed from the position picker, not collected separately
// in the UI (founder direction).
export const inferPlayerSide = (position) => {
  if (!position || !position.primary) return null;
  const { primary, secondary, tertiary } = position;

  // Standing / Clinch — both players are standing.
  if (primary === 'Standing / Clinch') return 'Neutral';

  // Guard primary is bottom by definition (top of guard = Passing primary).
  if (primary === 'Guard') return 'Bottom';

  // Passing primary is top by definition.
  if (primary === 'Passing') return 'Top';

  // Back primary — secondary tells us offense (Top) vs defense (Bottom).
  if (primary === 'Back') {
    if (!secondary) return 'Top';                    // default attacker
    if (/^Defense|Escapes?$/i.test(secondary)) return 'Bottom';
    return 'Top';
  }

  // Leg Entanglements — neutral by nature unless tertiary says otherwise.
  if (primary === 'Leg Entanglements') {
    if (tertiary === 'Attacking') return 'Top';
    if (tertiary === 'Defending' || tertiary === 'Escaping') return 'Bottom';
    return 'Neutral';
  }

  // All other primaries (Side Control, Mount, Knee on Belly, Turtle)
  // encode side in the secondary prefix.
  if (!secondary) return null;
  if (/^Top/i.test(secondary)) return 'Top';
  if (/^Bottom/i.test(secondary)) return 'Bottom';

  return null;
};

export const newStep = () => ({ text: '', needsDrilling: false });

// A blank sparring block — used when conditional reveal first triggers.
// Numeric stat fields are strings so empty inputs display as the input's
// placeholder (not a sticky "0"). Save handler coerces to Number.
export const newSparring = () => ({
  rolls: '',
  partners: [],
  subsLanded: '',
  subsCaught: '',
  sweepsLanded: '',
  sweepsAllowed: '',
  passesLanded: '',
  passesAllowed: '',
  takedownsLanded: '',
  takedownsConceded: '',
  feltScale: 3,
  note: '',
});

export const newReflection = () => ({
  intentHit: null,
  win: '',
  gap: '',
  nextFocus: '',
});

export const newInjury = () => ({ bodyPart: '', note: '' });
